import { Popover, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";

import { ProfileIcon } from "../../assets";

import { createOrder, getTerminalId } from "../../services";
import { getFirebaseRealTimeDatabase } from "../../services/firebaseRealTimeDatabase";
import { getWalletByClient } from "../../services/totem";
import { PaymentMethod } from "../../types";

import { useStore } from "../../store";
import { isWhatPercentOf } from "../../utils/isWhatPercentOf";
import { getPurchaseAvailability, getCheckoutErrorMessage } from "../../utils/getPurchaseAvailability";
import {
  Progress,
  PurchaseView,
  Button,
  FinishModal,
  PaymentMethodModal,
  BlockedModal,
} from "../index";

import useStyles from "./styles";
import { ClientProps } from "./client.types";
import { VisitorPaymentMethodModal } from "../Modals/VisitorPaymentMethodModal";
import { IndividualPaymentMethodModal } from "../Modals/IndividualPaymentMethodModal";
import { StudentPaymentMethodModal } from "../Modals/StudentPaymentMethodModal";
import { useEntity } from "../../hooks/useEntity";
import {
  getOrderReference,
  printOrderReceiptIfAvailable,
} from "../../hooks/usePrinter";

export const Client = (props: ClientProps) => {
  const {
    userManager, userTotem, order, navigation
  } = useStore();

  const Styles = useStyles({ picture: userTotem.picture });

  const oneSeconds = 1000;
  const thirtySeconds = oneSeconds * 30;

  const [openFinishModal, setOpenFinishModal] = useState<boolean>(false);
  const [openPaymentMethodModal, setOpenPaymentMethodModal] =
    useState<boolean>(false);

  const [
    openIndividualPaymentMethodModal,
    setOpenIndividualPaymentMethodModal,
  ] = useState<boolean>(false);

  const [openStudentPaymentModal, setOpenStudentPaymentModal] =
    useState<boolean>(false);

  const [countdown, setCountdown] = useState(0);

  const {
    allowPurchaseWithNegativeBalance,
    allowStudentCardMachinePurchase,
    isPixEnabledOnTotem,
  } = useEntity();

  const [isFinalizePurchaseLoading, setIsFinalizePurchaseLoading] =
    useState(false);
  const [menuIsOpened, setMenuIsOpened] = useState(false);
  const [openBlockedModal, setOpenBlockedModal] = useState<boolean>(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [openVisitorCheckoutModal, setOpenVisitorCheckoutModal] =
    useState<boolean>(false);

  const loadWallet = async () => {
    if (userTotem.id) {
      const wallet = await getWalletByClient(userTotem.id);

      if (wallet) userTotem.updateWallet(wallet);
    }
  };

  const availability = getPurchaseAvailability({
    cardBalanceInCents: userTotem.virtualCard?.balance ?? 0,
    totalAmountInCents: order.totalAmount,
    period: userTotem.wallet.period,
    periodLimit: userTotem.wallet.periodLimit,
    spent: userTotem.wallet.spent,
    allowPurchaseWithNegativeBalance,
  });

  const isBlockedProductLimit = (): boolean =>
    !availability.sufficient && !!userTotem.id;

  const isVisitor = (): boolean => !!props?.visitor;
  const finalizePurchase = async () => {
    setCheckoutError("");
    setIsFinalizePurchaseLoading(true);
    // TODO: Olhar aqui
    const data = {
      entity: userManager.entity,
      virtualCard: userTotem.virtualCard?.id,
      totalAmount: order.totalAmount,
      clientName: userTotem.name,
      isOnCredit: order.isOnCredit,
      authMethod: userTotem.loginMethod ?? 'none',
      items: order.items.map((item) => {
        const {
          id, name, amount, totalAmount, price, observation
        } = item;

        return {
          id,
          name,
          quantity: amount,
          price,
          totalAmount,
          observation,
        };
      }),
    };

    const receipt = {
      clientName: userTotem.name || order.clientName || "Cliente",
      totalAmount: order.totalAmount,
      items: order.items.map((item) => ({ ...item })),
    };
    const res = await createOrder(data);

    if (!res.error) {
      const orderReference = getOrderReference(res);
      if (orderReference) {
        printOrderReceiptIfAvailable(receipt, orderReference);
      } else {
        console.error("Receipt printing skipped", {
          orderReference,
          stage: "order-reference",
          message: "Successful order response did not contain an order ID",
        });
      }
      setOpenFinishModal(true);
      setIsFinalizePurchaseLoading(false);
    } else {
      setCheckoutError(getCheckoutErrorMessage(res.message));
      setOpenBlockedModal(true);
      loadWallet();
      setIsFinalizePurchaseLoading(false);
    }
  };

  const handleFinalizePurchase = () => {
    if (isVisitor()) {
      setOpenVisitorCheckoutModal(true);
      return;
    }

    if (props?.isMarket) {
      setOpenVisitorCheckoutModal(true);
      return;
    }

    if (userTotem?.role === "adminClient") {
      setOpenIndividualPaymentMethodModal(true);
      return;
    }

    if (allowStudentCardMachinePurchase && getTerminalId() && userTotem.id) {
      setOpenStudentPaymentModal(true);
    } else if (isBlockedProductLimit()) {
      setCheckoutError("");
      setOpenBlockedModal(true);
    } else if (userTotem.id) {
      finalizePurchase();
    } else {
      setOpenPaymentMethodModal(true);
    }
  };

  const handleStudentPaymentMethod = (method: PaymentMethod) => {
    setOpenStudentPaymentModal(false);
    navigation.navigateTo("/student-confirm-purchase", {
      state: {
        paymentMethod: method,
        virtualCardId: userTotem.virtualCard?.id,
      },
    });
  };

  const handleStudentSelectBalance = () => {
    setOpenStudentPaymentModal(false);
    if (isBlockedProductLimit()) {
      setCheckoutError("");
      setOpenBlockedModal(true);
      return;
    }
    finalizePurchase();
  };

  const handleLogout = async () => {
    navigation.navigateTo("/");

    userTotem.logout();
  };

  const onIdle = async () => {
    if (userTotem.id) {
      await handleLogout();
    }
  };

  const { getRemainingTime } = useIdleTimer({
    timeout: thirtySeconds,
    onIdle,
  });

  const getLeftTime = (): number => Math.ceil(getRemainingTime() / oneSeconds);

  useEffect(() => {
    setInterval(() => {
      setCountdown(getLeftTime());
    }, oneSeconds);
  }, []);

  useEffect(() => {
    getFirebaseRealTimeDatabase(userManager.entity, loadWallet);
  }, []);

  return (
    <div className={Styles.clientView}>
      {!props?.visitor &&
        (!props?.isMarket || (props?.isMarket && userTotem.id)) && (
          <div className={Styles.client}>
            <div className={Styles.clientInfo}>
              <Typography component="span">
                {userTotem.name || "Gerente"}
              </Typography>

              <Progress
                percentage={
                  isWhatPercentOf(
                    userTotem.wallet?.periodLimit,
                    userTotem.wallet?.spent
                  ) || 0
                }
              />
            </div>
            {userTotem.id && (
              <Popover
                onClose={() => setMenuIsOpened(false)}
                open={menuIsOpened}
                style={{ marginTop: 80 }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <div
                  onClick={() => {
                    setMenuIsOpened(false);
                    navigation.navigateTo("/update-password");
                  }}
                  className={Styles.submenuButton}
                >
                  <div className={Styles.submenuText}>
                    <Typography component="span">Editar</Typography>
                  </div>
                </div>
              </Popover>
            )}

            {userTotem.picture ? (
              <div
                className={Styles.clientPhoto}
                onClick={() => setMenuIsOpened(!menuIsOpened)}
              />
            ) : (
              <div
                className={Styles.picture}
                onClick={() => setMenuIsOpened(!menuIsOpened)}
              >
                <ProfileIcon />
              </div>
            )}
          </div>
      )}

      <PurchaseView
        totem={userTotem}
        order={order}
        onShowSectors={props?.onShowSectors}
      />

      <div className={Styles.footer}>
        <Button
          color="primary"
          className={Styles.button}
          disabled={order.items.length === 0 || isFinalizePurchaseLoading}
          onClick={handleFinalizePurchase}
        >
          Finalizar Compra
        </Button>

        <Button
          color="secondary"
          className={Styles.button}
          onClick={handleLogout}
        >
          {userTotem.id ? `Sair automaticamente em ${countdown}s` : "Sair"}
        </Button>
      </div>

      <BlockedModal
        message={checkoutError || availability.message}
        open={openBlockedModal}
        onClose={() => setOpenBlockedModal(false)}
      />

      <FinishModal open={openFinishModal} />
      <PaymentMethodModal open={openPaymentMethodModal} />
      <VisitorPaymentMethodModal
        open={openVisitorCheckoutModal}
        onClose={() => setOpenVisitorCheckoutModal(false)}
        isMarket={props?.isMarket}
        isPixEnabled={isPixEnabledOnTotem}
      />
      <IndividualPaymentMethodModal
        open={openIndividualPaymentMethodModal}
        isPixEnabled={isPixEnabledOnTotem}
      />
      <StudentPaymentMethodModal
        open={openStudentPaymentModal}
        onClose={() => setOpenStudentPaymentModal(false)}
        onSelectBalance={handleStudentSelectBalance}
        onSelectPaymentMethod={handleStudentPaymentMethod}
        balanceSufficient={availability.sufficient}
        isPixEnabled={isPixEnabledOnTotem}
      />
    </div>
  );
};
